import { Navigation } from './Navigation';
import { HeaderNavigation } from './types';

/**
 * The handler for the mobile navigation of the header.
 */
export class MobileNavigation extends Navigation implements HeaderNavigation {
  backButtons: NodeListOf<HTMLButtonElement>;

  /**
   *
   * @param headerEl      The <header /> element the navigation is part of
   * @param navigationEl  The <nav /> element containing the links we have to manage
   */
  constructor(headerEl: HTMLElement, navigationEl: HTMLElement) {
    super(headerEl, navigationEl);

    //
    this.links = this.navigationEl.querySelectorAll(
      'a.header__navigation-link[data-has-subnavigation]',
    );
    //
    this.backButtons = this.navigationEl.querySelectorAll(
      'button.header__subnavigation-title',
    );

    // bind handlers to this
    this.handleNavigationLinkClick = this.handleNavigationLinkClick.bind(this);
    this.handleBackButtonClick = this.handleBackButtonClick.bind(this);

    // add event listeners
    this.addEventListeners();
  }

  /**
   *
   * @protected
   */
  protected addEventListeners() {
    this.links?.forEach((link) => {
      (link as HTMLLinkElement).addEventListener(
        'click',
        this.handleNavigationLinkClick,
      );
    });

    this.backButtons?.forEach((button) => {
      (button as HTMLButtonElement).addEventListener(
        'click',
        this.handleBackButtonClick,
      );
    });
  }

  /**
   *
   * @protected
   */
  protected removeEventListeners() {
    this.links?.forEach((link) => {
      (link as HTMLLinkElement).removeEventListener(
        'click',
        this.handleNavigationLinkClick,
      );
    });

    this.backButtons?.forEach((button) => {
      (button as HTMLButtonElement).removeEventListener(
        'click',
        this.handleBackButtonClick,
      );
    });
  }

  /**
   * Open the subnavigation of the given navigation item. Mainly this will
   * hide the link inside the item, the other items on the same level and show
   * the subnavigation of the clicked/given item.
   *
   * @param item  The li.header__navigation-item element to open the subnavigation for.
   */
  openSubnavigation(item: HTMLElement) {
    // get the ul- element of the clicked navigation item (li)
    const container = item.closest('ul.header__navigation-items');
    // query the navigation items of the ul- element
    const items = container?.querySelectorAll('& > .header__navigation-item');
    // query the link of the clicked navigation item
    const link = item?.querySelector(
      'a.header__navigation-link',
    ) as HTMLLinkElement;
    // query the subnavigation of the clicked navigation item
    // which holds the next level of navigation items
    const subnavigation = item?.querySelector(
      'div.header__subnavigation',
    ) as HTMLElement;
    // query the parent subnavigation of the clicked navigation item
    const parentSubNavigation = container?.closest('div.header__subnavigation');

    if (!container || !link || !subnavigation || !items) return;

    // mark the clicked navigation item as active
    item.classList.add('header__navigation-item--active');

    // loop over all the navigation-items (li's) of the ul- element
    // containing the clicked navigation item.
    for (const i of items) {
      // if the navigation item is not the clicked navigation item
      // and not active, hide it.
      if (
        i !== item &&
        !i.classList.contains('header__navigation-item--active')
      ) {
        i.classList.add('header__navigation-item--inactive');
      }
    }

    // if we're on the third level we need to check whether we have a parent
    // subnavigation so we can hide the title of it
    if (parentSubNavigation) {
      const title = parentSubNavigation.querySelector(
        '.header__subnavigation-title',
      ) as HTMLButtonElement;
      if (title) {
        title.classList.add('header__subnavigation-title--inactive');
      }
    }
  }

  /**
   * Close the given subnavigation. This will show the link of the clicked item
   * link, show all other navigation items and hide the subnavigation on the
   * current level.
   *
   * @param subnavigation The div.header__subnavigation element to close.
   */
  closeSubnavigation(subnavigation: HTMLDivElement) {
    // query the navigation item (li) which contains the given subnavigation
    const item = subnavigation?.closest(
      'li.header__navigation-item',
    ) as HTMLLIElement;
    // query the ul- element of the navigation item containing the subnavigation
    const container = item?.closest(
      'ul.header__navigation-items',
    ) as HTMLUListElement;
    // query the link inside the navigation item containing the subnavigation
    const link = item?.querySelector(
      'a.header__navigation-link',
    ) as HTMLLinkElement;
    // query all the navigation items (li's) of the ul- element containing the subnavigation
    const items = container?.querySelectorAll('& > .header__navigation-item');
    // query the parent subnavigation of the clicked navigation item
    const parentSubNavigation = container?.closest('div.header__subnavigation');

    if (!item || !container || !link) return;

    // deactivate the clicked navigation item
    item.classList.remove('header__navigation-item--active');

    // activate all others
    for (const i of items) {
      i.classList.remove('header__navigation-item--inactive');
    }

    // if we're on the third level we need to check whether we have a parent
    // subnavigation so we must show the title of it
    if (parentSubNavigation) {
      const title = parentSubNavigation.querySelector(
        '.header__subnavigation-title',
      ) as HTMLButtonElement;
      if (title) {
        title.classList.remove('header__subnavigation-title--inactive');
      }
    }
  }

  /**
   * Callback method to be called when the navigation was closed. This allows us
   * to do some cleanup.
   */
  onNavigationClose() {
    // get all navigation items (li's) of the mobile navigation
    const items = this.navigationEl.querySelectorAll(
      '.header__navigation-item',
    );
    // loop over all navigation items and remove the active and inactive classes
    for (const item of items) {
      item.classList.remove('header__navigation-item--active');
      item.classList.remove('header__navigation-item--inactive');
    }

    // get all subnavigation titles (button.header__subnavigation-title) of
    // the mobile navigation
    const titles = this.navigationEl.querySelectorAll(
      '.header__subnavigation-title',
    );
    // loop over all titles and remove the inactive class
    for (const title of titles) {
      title.classList.remove('header__subnavigation-title--inactive');
    }
  }

  /**
   *
   * @param e
   */
  handleNavigationLinkClick(e: MouseEvent) {
    e.preventDefault();

    // get the navigation item (li) which contains the clicked link
    const navItem = (e.target as HTMLLinkElement).closest(
      '.header__navigation-item',
    );

    // if not active, open the subnavigation
    if (!navItem?.classList.contains('header__navigation-item--active')) {
      this.openSubnavigation(navItem as HTMLElement);
    }
  }

  /**
   *
   * @param e
   */
  handleBackButtonClick(e: MouseEvent) {
    // get the subnavigation (div) which contains the clicked back button
    const subnavigation = (e.target as HTMLButtonElement).closest(
      'div.header__subnavigation',
    );

    this.closeSubnavigation(subnavigation as HTMLDivElement);
  }
}
